import React from "react";
import { Link } from "react-router-dom";
import { Mail, MessageCircle, Phone } from "lucide-react";
import Logo from "src/components/common/Logo";
import { SITE_SETTINGS } from "src/constants/common";
import { trackNavClick, trackWhatsappClick, trackEvent } from "src/utils/analytics";

const waLink = `https://wa.me/${SITE_SETTINGS.CONTACT_NUMBER.replace(/[^0-9]/g, "")}`;

const FOOTER_COLUMNS: { heading: string; links: { label: string; to: string }[] }[] = [
  {
    heading: "Product",
    links: [
      { label: "Home", to: "/" },
      { label: "Pricing", to: "/pricing" },
      { label: "FAQs", to: "/faqs" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Terms & Conditions", to: "/terms" },
      { label: "Privacy Policy", to: "/privacy" },
    ],
  },
];

const Footer: React.FC = () => {
  return (
    <footer className="border-t border-gray-100 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-16 grid gap-12 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <Link to="/" className="flex items-center gap-2 w-fit">
            <Logo variant="contained" size="small" />
            <Logo variant="text" size="large" />
          </Link>
          <p className="mt-4 max-w-xs text-sm text-gray-500">
            Inventory, billing, and warehouse operations in one place —
            built for small and growing Indian businesses.
          </p>
          <div className="mt-5 space-y-2 text-sm text-gray-600">
            <a
              href={`mailto:${SITE_SETTINGS.CONTACT_EMAIL}`}
              onClick={() => trackEvent("email_click", { location: "footer" })}
              className="flex items-center gap-2 hover:text-primary-600"
            >
              <Mail size={15} className="text-gray-400" />
              {SITE_SETTINGS.CONTACT_EMAIL}
            </a>
            <a
              href={`tel:${SITE_SETTINGS.CONTACT_NUMBER.replace(/\s/g, "")}`}
              onClick={() => trackEvent("phone_click", { location: "footer" })}
              className="flex items-center gap-2 hover:text-primary-600"
            >
              <Phone size={15} className="text-gray-400" />
              {SITE_SETTINGS.CONTACT_NUMBER}
            </a>
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackWhatsappClick("footer")}
              className="flex items-center gap-2 hover:text-emerald-600"
            >
              <MessageCircle size={15} className="text-gray-400" />
              Chat on WhatsApp
            </a>
          </div>
        </div>

        {FOOTER_COLUMNS.map((col) => (
          <div key={col.heading}>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              {col.heading}
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              {col.links.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    onClick={() => trackNavClick(l.label, "footer")}
                    className="text-gray-600 hover:text-primary-600"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-400">
          <span>
            © {new Date().getFullYear()} {SITE_SETTINGS.NAME}. All rights reserved.
          </span>
          <span>Built for Indian businesses · GST-ready workflows</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
